import { Modal } from '@mantine/core'
import { Button } from 'components/Button/Button'
import { translate } from 'i18n/i18n'
import { useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterGroup } from 'stores/fieldAssetStore/helperTypes'
import { apiFetch } from 'utils/apiFetch'
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage'
import { useErrorMessage } from '../../../ErrorMessage/useErrorMessage'

export const DeleteEmitterGroupModal = ({
  selectedZoneGroupDelete,
  setSelectedZoneGroupDelete,
}: {
  selectedZoneGroupDelete: (TIrrigationZoneEmitterGroup & { propertyId: number }) | null
  setSelectedZoneGroupDelete: React.Dispatch<
    React.SetStateAction<(TIrrigationZoneEmitterGroup & { propertyId: number }) | null>
  >
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { showErrorMessage, triggerErrorMessage, onCloseButtonPress } = useErrorMessage()
  const closeModal = () => setSelectedZoneGroupDelete(null)

  return (
    <Modal
      title={
        <div>
          <h4>{translate.phrases.banyanApp('Delete Group')}</h4>
        </div>
      }
      opened={!!selectedZoneGroupDelete}
      onClose={closeModal}
    >
      {!!selectedZoneGroupDelete && (
        <>
          <ErrorMessage
            message={showErrorMessage}
            onCloseButtonPress={onCloseButtonPress}
            showErrorMessage={!!showErrorMessage}
          />
          <div>
            <translate.Phrases.banyanApp
              k="Are you sure you want to delete <name>{{name}}</name>?"
              c={{ name: <strong /> }}
              v={{ name: selectedZoneGroupDelete.groupName }}
            />
          </div>
          <div css={{ marginTop: 20, marginBottom: 30 }}>
            {translate.phrases.banyanApp('Deleting this group will remove it from your list.')}
          </div>
          <div css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="link" onClick={closeModal}>
              {translate.phrases.banyanApp('Cancel')}
            </Button>
            <Button
              disabled={loading}
              onClick={async () => {
                if (loading) return

                try {
                  setLoading(true)

                  const response = await apiFetch({
                    url: '/irrigation-zone-emitter-group-delete',
                    body: { groupId: selectedZoneGroupDelete.groupId },
                  })

                  const success = typeof response === 'number'

                  if (success) {
                    fieldAssetStore.setState((s) => {
                      if (!s || !selectedZoneGroupDelete.propertyId) return s

                      const irrigationZoneEmitterGroupsWithDeletedGroupRemoved = Object.entries(
                        s.properties?.[selectedZoneGroupDelete.propertyId]?.irrigationZoneEmitterGroups ?? {},
                      ).reduce<Record<string, TIrrigationZoneEmitterGroup>>((acc, [groupId, group]) => {
                        if (Number(groupId) !== selectedZoneGroupDelete.groupId) {
                          acc[groupId] = group
                        }

                        return acc
                      }, {})

                      return {
                        ...s,
                        properties: {
                          ...s.properties,
                          [String(selectedZoneGroupDelete.propertyId)]: {
                            ...s.properties?.[selectedZoneGroupDelete.propertyId],
                            irrigationZoneEmitterGroups: Object.keys(
                              irrigationZoneEmitterGroupsWithDeletedGroupRemoved,
                            ).length
                              ? irrigationZoneEmitterGroupsWithDeletedGroupRemoved
                              : null,
                          },
                        },
                      }
                    })

                    closeModal()
                  } else {
                    triggerErrorMessage(translate.phrases.banyanApp('Failed to delete group'))
                  }
                } catch (e) {
                  triggerErrorMessage(translate.phrases.banyanApp('Failed to delete group'))
                } finally {
                  setLoading(false)
                }
              }}
            >
              {translate.phrases.banyanApp('Delete Group')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
